import type {
  BimanualTokenName,
  BothHandsTokenResult,
  CanvasBoundsType,
  ClenchData,
  CombinedResults,
  HandTokenizerResults,
  HandTokenName,
  HitBox,
  MediapipeLandmark,
  PinchGestureStreamOutput,
  SingleHandTokenResult,
} from "/@/shared/motion-menu-system/MenuTypes";

import {
  addEventListenerHashParamJson,
  setHashParamValueJsonInWindow,
} from "@metapages/hash-query";
import { HashParamDefinition } from "@metapages/metapage";

import type {
  ControllerOptionsConfig,
  MotionController,
  MotionControllerParameters,
} from "../controllers/types.ts";
import { angleBetween3DCoords, getCorrectedHandLandmarks } from "./common.ts";

const PinchDown = "PinchDown" as const;
const PinchMove = "PinchMove" as const;
const PinchUp = "PinchUp" as const;

// === Configuration ===
const FINGER_EXTENDED_THRESHOLD = 2.5; // PIP joint angle (rad) → finger extended
const THUMB_EXTENDED_THRESHOLD = 2.3; // IP joint angle (rad) → thumb extended
const PINCH_WORLD_DISTANCE = 0.05; // world-space units, thumb-tip to finger-tip
const PINCH_WORLD_DISTANCE_MULTI = 0.06; // tighter threshold for multi-finger pinches
const PALM_FACING_CAMERA_MIN = 0.65; // min |normal.z| for index-finger pinch. below this, no pinches are detected (hand too edge-on)
const PALM_FACING_CAMERA_MIN_MULTI = 0.65; // min |normal.z| for multi-finger pinch
const PINCH_MAX_HAND_SPEED = 0.4; // max wrist speed (world-units/sec, ~meters/sec) to be considered "settled"
const PINCH_SETTLE_TIME = 120; // ms hand must be continuously below speed threshold before pinches are allowed
const SPLAY_HIGH_THRESHOLD = 0.35; // avg inter-finger angle (rad) → open palm
const SPLAY_LOW_THRESHOLD = 0.18; // avg inter-finger angle (rad) → flat palm

// === MediaPipe 21 landmark indices ===
const WRIST = 0;
const THUMB_CMC = 1,
  THUMB_MCP = 2,
  THUMB_IP = 3,
  THUMB_TIP = 4;
const INDEX_MCP = 5,
  INDEX_PIP = 6,
  INDEX_DIP = 7,
  INDEX_TIP = 8;
const MIDDLE_MCP = 9,
  MIDDLE_PIP = 10,
  MIDDLE_DIP = 11,
  MIDDLE_TIP = 12;
const RING_MCP = 13,
  RING_PIP = 14,
  RING_DIP = 15,
  RING_TIP = 16;
const PINKY_MCP = 17,
  PINKY_PIP = 18,
  PINKY_DIP = 19,
  PINKY_TIP = 20;

const ALL_LANDMARKS = [
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
];

// === Debug colors ===
const TOKEN_COLORS: Record<HandTokenName, string> = {
  wholeHand: "rgba(180,180,180,0.35)",
  palmNormal: "rgba(180,180,180,0.35)",
  handsVector: "rgba(180,180,180,0.35)",
  wristRotation: "rgba(160,80,220,0.35)",
  wristAngle: "rgba(255,160,40,0.35)",
  fist: "rgba(255,80,80,0.35)",
  openPalm: "rgba(80,220,80,0.35)",
  flatPalm: "rgba(80,180,80,0.35)",
  pointing: "rgba(80,80,255,0.35)",
  peace: "rgba(200,80,220,0.35)",
  threeUp: "rgba(220,200,80,0.35)",
  fourUp: "rgba(80,220,220,0.35)",
  thumbUp: "rgba(255,200,0,0.35)",
  pinch: "rgba(255,140,40,0.35)",
  ok: "rgba(40,255,140,0.35)",
};

const BIMANUAL_COLORS: Record<BimanualTokenName, string> = {
  bothFists: "rgba(255,80,80,0.25)",
  bothOpen: "rgba(80,220,80,0.25)",
  handsTogether: "rgba(220,220,80,0.25)",
};

// === Debug option key map ===
const TOKEN_DEBUG_KEY: Partial<
  Record<HandTokenName, keyof HandTokenizerOptions>
> = {
  wholeHand: "debugWholeHand",
  wristRotation: "debugWristRotation",
  wristAngle: "debugWristAngle",
  handsVector: "debugHandsVector",
  fist: "debugFist",
  openPalm: "debugPalms",
  flatPalm: "debugPalms",
  pointing: "debugPointing",
  peace: "debugFingers",
  threeUp: "debugFingers",
  fourUp: "debugFingers",
  thumbUp: "debugFingers",
  ok: "debugFingers",
};

// === Options ===

export const DefaultHandTokenizerControllerOptions: Record<
  string,
  HashParamDefinition
> = {
  enable: {
    type: "boolean",
    default: { value: true },
    label: "Enable Hand Tokenizer",
  },
  debug: {
    type: "boolean",
    default: { value: false },
    label: "Debug Display (master)",
  },
  debugWholeHand: {
    type: "boolean",
    default: { value: false },
    label: "Show Whole Hand",
  },
  debugWristRotation: {
    type: "boolean",
    default: { value: false },
    label: "Show Wrist Rotation",
  },
  debugWristAngle: {
    type: "boolean",
    default: { value: false },
    label: "Show Wrist Angle",
  },
  debugHandsVector: {
    type: "boolean",
    default: { value: false },
    label: "Show Hands Vector",
  },
  debugFist: {
    type: "boolean",
    default: { value: false },
    label: "Show Fist",
  },
  debugPalms: {
    type: "boolean",
    default: { value: false },
    label: "Show Palms",
  },
  debugPointing: {
    type: "boolean",
    default: { value: false },
    label: "Show Pointing",
  },
  debugFingers: {
    type: "boolean",
    default: { value: false },
    label: "Show Finger Gestures",
  },
  debugPinchTwoFinger: {
    type: "boolean",
    default: { value: false },
    label: "Show Two-Finger Pinch",
  },
  debugPinchMultiFinger: {
    type: "boolean",
    default: { value: false },
    label: "Show Multi-Finger Pinch",
  },
  debugBimanual: {
    type: "boolean",
    default: { value: false },
    label: "Show Bimanual",
  },
  renderSinglePalmNormal: {
    type: "boolean",
    default: { value: false },
    label: "Render Single Palm Normal",
  },
  renderBothPalmNormals: {
    type: "boolean",
    default: { value: false },
    label: "Render Both Palm Normals",
  },
  renderTwoHandAngle: {
    type: "boolean",
    default: { value: false },
    label: "Render Two-Hand Angle",
  },
};

const optionsKey = "handTokenizer";
export const controllerOptionsConfig: ControllerOptionsConfig = {
  key: optionsKey,
  label: "Hand Tokenizer",
  optionDefinitions: DefaultHandTokenizerControllerOptions,
};

export type HandTokenizerOptions = {
  enable?: boolean;
  debug?: boolean;
  debugWholeHand?: boolean;
  debugWristRotation?: boolean;
  debugWristAngle?: boolean;
  debugHandsVector?: boolean;
  debugFist?: boolean;
  debugPalms?: boolean;
  debugPointing?: boolean;
  debugFingers?: boolean;
  debugPinchTwoFinger?: boolean;
  debugPinchMultiFinger?: boolean;
  debugBimanual?: boolean;
  renderSinglePalmNormal?: boolean;
  renderBothPalmNormals?: boolean;
  renderTwoHandAngle?: boolean;
};

// === Pure helpers ===

function getSubHitBox(lm: MediapipeLandmark[], indices: number[]): HitBox {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const i of indices) {
    const p = lm[i];
    if (!p) continue;
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  if (!isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function boxCentroid(box: HitBox): { x: number; y: number } {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

function unionHitBox(a: HitBox, b: HitBox): HitBox {
  const minX = Math.min(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxX = Math.max(a.x + a.width, b.x + b.width);
  const maxY = Math.max(a.y + a.height, b.y + b.height);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function hitboxesOverlap(a: HitBox, b: HitBox): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

function toScreenPercent(
  box: HitBox,
  bounds: CanvasBoundsType | undefined,
  containerRect: DOMRect | undefined,
): { left: number; top: number; width: number; height: number } {
  if (!bounds || !containerRect) {
    return {
      left: box.x * 100,
      top: box.y * 100,
      width: box.width * 100,
      height: box.height * 100,
    };
  }
  const { renderedX, renderedY, renderedWidth, renderedHeight } = bounds;
  return {
    left: ((renderedX - containerRect.left + box.x * renderedWidth) /
      containerRect.width) *
      100,
    top: ((renderedY - containerRect.top + box.y * renderedHeight) /
      containerRect.height) *
      100,
    width: ((box.width * renderedWidth) / containerRect.width) * 100,
    height: ((box.height * renderedHeight) / containerRect.height) * 100,
  };
}

function toScreenPointPercent(
  point: { x: number; y: number },
  bounds: CanvasBoundsType | undefined,
  containerRect: DOMRect | undefined,
): { left: number; top: number } {
  if (!bounds || !containerRect) {
    return { left: point.x * 100, top: point.y * 100 };
  }
  const { renderedX, renderedY, renderedWidth, renderedHeight } = bounds;
  return {
    left: ((renderedX - containerRect.left + point.x * renderedWidth) /
      containerRect.width) *
      100,
    top: ((renderedY - containerRect.top + point.y * renderedHeight) /
      containerRect.height) *
      100,
  };
}

// === Palm normal computation (matches 3dRotationControl algorithm) ===

function computePalmNormal(worldLm: MediapipeLandmark[]): {
  normal: { x: number; y: number; z: number };
  normalArray: [number, number, number];
  angle: number;
  facing: "palmar" | "dorsal";
} {
  const wrist = worldLm[WRIST];
  const indexBase = worldLm[INDEX_MCP];
  const pinkyBase = worldLm[PINKY_MCP];

  // wrist→index × wrist→pinky (same as 3dRotationControl.calculatePalmNormal)
  const v1: [number, number, number] = [
    indexBase.x - wrist.x,
    indexBase.y - wrist.y,
    indexBase.z - wrist.z,
  ];
  const v2: [number, number, number] = [
    pinkyBase.x - wrist.x,
    pinkyBase.y - wrist.y,
    pinkyBase.z - wrist.z,
  ];

  const cx = v1[1] * v2[2] - v1[2] * v2[1];
  const cy = v1[2] * v2[0] - v1[0] * v2[2];
  const cz = v1[0] * v2[1] - v1[1] * v2[0];
  const len = Math.sqrt(cx * cx + cy * cy + cz * cz);

  const normalArray: [number, number, number] = len > 0
    ? [cx / len, cy / len, cz / len]
    : [0, 0, 0];

  const normal = {
    x: normalArray[0],
    y: normalArray[1],
    z: normalArray[2],
  };
  const angle = len > 0 ? Math.atan2(normal.x, normal.z) : 0;
  const facing: "palmar" | "dorsal" = Math.abs(angle) < Math.PI / 2
    ? "palmar"
    : "dorsal";

  return { normal, normalArray, angle, facing };
}

function normalToEulerAngles(normal: [number, number, number]): {
  pitch: number;
  yaw: number;
  roll: number;
} {
  const [nx, ny, nz] = normal;
  const pitch = Math.atan2(-ny, Math.sqrt(nx * nx + nz * nz));
  const yaw = Math.atan2(nx, nz);
  const roll = 0;
  return { pitch, yaw, roll };
}

function vec3Normalize(v: [number, number, number]): [number, number, number] {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (len === 0) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

function calculate2DAngle(
  from: { x: number; y: number },
  to: { x: number; y: number },
): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

function calculatePalmCenterScreen(screenLm: MediapipeLandmark[]): {
  x: number;
  y: number;
} {
  return {
    x: (screenLm[WRIST].x + screenLm[INDEX_MCP].x + screenLm[PINKY_MCP].x) / 3,
    y: (screenLm[WRIST].y + screenLm[INDEX_MCP].y + screenLm[PINKY_MCP].y) / 3,
  };
}

// === Canvas rendering helpers ===

function mapNormalizedToCanvas(
  point: { x: number; y: number },
  canvasBounds: CanvasBoundsType | undefined,
  rootRect: DOMRect,
  dpr: number,
  flip: boolean,
): { x: number; y: number } {
  let lx: number;
  let ly: number;
  if (canvasBounds) {
    const localLeft = canvasBounds.renderedX - rootRect.left;
    const localTop = canvasBounds.renderedY - rootRect.top;
    lx = localLeft + point.x * canvasBounds.renderedWidth;
    ly = localTop + point.y * canvasBounds.renderedHeight;
  } else {
    lx = point.x * rootRect.width;
    ly = point.y * rootRect.height;
  }
  if (flip) {
    lx = rootRect.width - lx;
  }
  return { x: lx * dpr, y: ly * dpr };
}

function drawArrowOnCanvas(
  ctx: CanvasRenderingContext2D,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  dpr: number,
): void {
  ctx.strokeStyle = "#87CEEB";
  ctx.fillStyle = "#87CEEB";
  ctx.lineWidth = 5 * dpr;

  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.stroke();

  // Arrowhead
  const headLength = 20 * dpr;
  const angle = Math.atan2(endY - startY, endX - startX);
  ctx.beginPath();
  ctx.moveTo(endX, endY);
  ctx.lineTo(
    endX - headLength * Math.cos(angle - Math.PI / 6),
    endY - headLength * Math.sin(angle - Math.PI / 6),
  );
  ctx.lineTo(
    endX - headLength * Math.cos(angle + Math.PI / 6),
    endY - headLength * Math.sin(angle + Math.PI / 6),
  );
  ctx.closePath();
  ctx.fill();

  // Circle at start
  ctx.beginPath();
  ctx.arc(startX, startY, 8 * dpr, 0, 2 * Math.PI);
  ctx.fill();
}

// === Gesture detection helpers ===

function computeFingerExtensions(lm: MediapipeLandmark[]): {
  index: boolean;
  middle: boolean;
  ring: boolean;
  pinky: boolean;
  thumb: boolean;
} {
  return {
    index: angleBetween3DCoords(lm[INDEX_MCP], lm[INDEX_PIP], lm[INDEX_DIP]) >
      FINGER_EXTENDED_THRESHOLD,
    middle:
      angleBetween3DCoords(lm[MIDDLE_MCP], lm[MIDDLE_PIP], lm[MIDDLE_DIP]) >
        FINGER_EXTENDED_THRESHOLD,
    ring: angleBetween3DCoords(lm[RING_MCP], lm[RING_PIP], lm[RING_DIP]) >
      FINGER_EXTENDED_THRESHOLD,
    pinky: angleBetween3DCoords(lm[PINKY_MCP], lm[PINKY_PIP], lm[PINKY_DIP]) >
      FINGER_EXTENDED_THRESHOLD,
    thumb: angleBetween3DCoords(lm[THUMB_MCP], lm[THUMB_IP], lm[THUMB_TIP]) >
      THUMB_EXTENDED_THRESHOLD,
  };
}

function computeSplay(lm: MediapipeLandmark[]): number {
  const pairs: [number, number, number, number][] = [
    [INDEX_MCP, INDEX_TIP, MIDDLE_MCP, MIDDLE_TIP],
    [MIDDLE_MCP, MIDDLE_TIP, RING_MCP, RING_TIP],
    [RING_MCP, RING_TIP, PINKY_MCP, PINKY_TIP],
  ];
  let sum = 0,
    count = 0;
  for (const [b1, t1, b2, t2] of pairs) {
    const v1x = lm[t1].x - lm[b1].x,
      v1y = lm[t1].y - lm[b1].y,
      v1z = lm[t1].z - lm[b1].z;
    const v2x = lm[t2].x - lm[b2].x,
      v2y = lm[t2].y - lm[b2].y,
      v2z = lm[t2].z - lm[b2].z;
    const m1 = Math.sqrt(v1x * v1x + v1y * v1y + v1z * v1z);
    const m2 = Math.sqrt(v2x * v2x + v2y * v2y + v2z * v2z);
    if (m1 > 0 && m2 > 0) {
      const dot = v1x * v2x + v1y * v2y + v1z * v2z;
      sum += Math.acos(Math.max(-1, Math.min(1, dot / (m1 * m2))));
      count++;
    }
  }
  return count > 0 ? sum / count : 0;
}

function computeThumbToFingerTipDistance(
  lm: MediapipeLandmark[],
  fingerTipIndex: number,
): number {
  const t = lm[THUMB_TIP],
    f = lm[fingerTipIndex];
  const dx = t.x - f.x,
    dy = t.y - f.y,
    dz = t.z - f.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function computePinchTypes(
  worldLm: MediapipeLandmark[],
  palmNormalZ: number,
): {
  indexFinger: boolean;
  indexMiddleFingers: boolean;
  allFingers: boolean;
} {
  const absFacing = Math.abs(palmNormalZ);

  // Hand is too edge-on to the camera — finger landmarks are unreliable
  if (absFacing < PALM_FACING_CAMERA_MIN) {
    return { indexFinger: false, indexMiddleFingers: false, allFingers: false };
  }

  const toIndex = computeThumbToFingerTipDistance(worldLm, INDEX_TIP);
  const indexFinger = toIndex < PINCH_WORLD_DISTANCE;

  // Multi-finger pinches need the palm more clearly visible
  if (absFacing < PALM_FACING_CAMERA_MIN_MULTI) {
    return { indexFinger, indexMiddleFingers: false, allFingers: false };
  }

  const toMiddle = computeThumbToFingerTipDistance(worldLm, MIDDLE_TIP);
  const toRing = computeThumbToFingerTipDistance(worldLm, RING_TIP);
  const toPinky = computeThumbToFingerTipDistance(worldLm, PINKY_TIP);
  const indexMiddleFingers = indexFinger &&
    toMiddle < PINCH_WORLD_DISTANCE_MULTI;
  const allFingers = indexMiddleFingers &&
    toRing < PINCH_WORLD_DISTANCE_MULTI &&
    toPinky < PINCH_WORLD_DISTANCE_MULTI;
  return { indexFinger, indexMiddleFingers, allFingers };
}

function computePinchCentroid(
  screenLm: MediapipeLandmark[],
  fingerTipIndices: number[],
): { x: number; y: number; z: number } {
  const thumb = screenLm[THUMB_TIP];
  let sumX = thumb.x,
    sumY = thumb.y,
    sumZ = thumb.z;
  for (const i of fingerTipIndices) {
    sumX += screenLm[i].x;
    sumY += screenLm[i].y;
    sumZ += screenLm[i].z;
  }
  const count = 1 + fingerTipIndices.length;
  return { x: sumX / count, y: sumY / count, z: sumZ / count };
}

// === Circular angle wrapping for angular velocity ===
function circularDelta(current: number, previous: number): number {
  let delta = current - previous;
  if (delta > Math.PI) delta -= 2 * Math.PI;
  if (delta < -Math.PI) delta += 2 * Math.PI;
  return delta;
}

// === Single-hand classification ===

type ClassifyHandResult = {
  result: SingleHandTokenResult;
  fullHitbox: HitBox;
  wristRotationAngle: number;
  palmNormalArray: [number, number, number];
  palmCenterScreen: { x: number; y: number };
};

function classifyHand(
  worldLm: MediapipeLandmark[],
  screenLm: MediapipeLandmark[],
  prevWristRotation: number | undefined,
  clenchSide?: {
    meanClenchAngle?: number;
    meanClenchAngleNormalized?: number;
    isClench?: boolean;
  },
): ClassifyHandResult {
  const f = computeFingerExtensions(worldLm);
  const splay = computeSplay(worldLm);
  const palm = computePalmNormal(worldLm);
  const pinch = computePinchTypes(worldLm, palm.normalArray[2]);

  const fullHitbox = getSubHitBox(screenLm, ALL_LANDMARKS);
  const palmCenterScreen = calculatePalmCenterScreen(screenLm);

  // Create result at the beginning
  const result: SingleHandTokenResult = {};

  // wholeHand — always present
  result.wholeHand = { hitbox: fullHitbox };

  // palmNormal — always present
  result.palmNormal = {
    angle: palm.angle,
    normal: palm.normal,
  };

  // wristRotation — angle + normal
  result.wristRotation = {
    angle: palm.angle,
    normal: palm.normal,
  };

  // wristAngle — angular velocity (frame delta with circular wrapping)
  const angularVelocity = prevWristRotation !== undefined
    ? circularDelta(palm.angle, prevWristRotation)
    : 0;
  result.wristAngle = {
    angle: palm.angle,
    angularVelocity,
  };

  // Fist — only emitted when clench angle data is available
  if (
    !f.index && !f.middle && !f.ring && !f.pinky &&
    clenchSide?.meanClenchAngle !== undefined &&
    clenchSide?.meanClenchAngleNormalized !== undefined
  ) {
    result.fist = {
      hitbox: fullHitbox,
      meanClenchAngle: clenchSide.meanClenchAngle,
      meanClenchAngleNormalized: clenchSide.meanClenchAngleNormalized,
    };
  }

  // Thumb up
  if (f.thumb && !f.index && !f.middle && !f.ring && !f.pinky) {
    result.thumbUp = {
      hitbox: getSubHitBox(screenLm, [
        THUMB_CMC,
        THUMB_MCP,
        THUMB_IP,
        THUMB_TIP,
      ]),
    };
  }

  // Pointing (index only extended)
  if (f.index && !f.middle && !f.ring && !f.pinky) {
    result.pointing = {
      centroid: { x: screenLm[INDEX_TIP].x, y: screenLm[INDEX_TIP].y },
      hitbox: getSubHitBox(screenLm, [
        INDEX_MCP,
        INDEX_PIP,
        INDEX_DIP,
        INDEX_TIP,
      ]),
    };
  }

  // Peace / V sign
  if (f.index && f.middle && !f.ring && !f.pinky) {
    result.peace = { hitbox: fullHitbox };
  }

  // Three fingers up
  if (f.index && f.middle && f.ring && !f.pinky) {
    result.threeUp = { hitbox: fullHitbox };
  }

  // Four fingers up (all main fingers extended)
  if (f.index && f.middle && f.ring && f.pinky) {
    result.fourUp = { hitbox: fullHitbox };
    if (splay > SPLAY_HIGH_THRESHOLD) {
      result.openPalm = {
        normal: palm.normal,
        surfaceVisible: palm.facing,
      };
    } else if (splay < SPLAY_LOW_THRESHOLD) {
      result.flatPalm = {
        normal: palm.normal,
        surfaceVisible: palm.facing,
      };
    }
  }

  // Pinch types
  if (pinch.indexFinger) {
    result.pinchIndexFinger = {
      centroid: computePinchCentroid(screenLm, [INDEX_TIP]),
    };
  }
  if (pinch.indexMiddleFingers) {
    result.pinchIndexMiddleFingers = {
      centroid: computePinchCentroid(screenLm, [INDEX_TIP, MIDDLE_TIP]),
    };
  }
  if (pinch.allFingers) {
    result.pinchAllFingers = {
      centroid: computePinchCentroid(screenLm, [
        INDEX_TIP,
        MIDDLE_TIP,
        RING_TIP,
        PINKY_TIP,
      ]),
    };
  }

  // Aggregate pinch: set to the most specific active pinch
  if (pinch.indexFinger) {
    result.pinch = result.pinchAllFingers ??
      result.pinchIndexMiddleFingers ??
      result.pinchIndexFinger;
  }

  // OK sign: pinch + other fingers extended
  if (pinch.indexFinger && f.middle && f.ring && f.pinky) {
    result.ok = { hitbox: fullHitbox };
  }

  return {
    result,
    fullHitbox,
    wristRotationAngle: palm.angle,
    palmNormalArray: palm.normalArray,
    palmCenterScreen,
  };
}

// === Bimanual classification ===

function classifyBothHands(
  leftResult: SingleHandTokenResult,
  leftHitbox: HitBox,
  rightResult: SingleHandTokenResult,
  rightHitbox: HitBox,
): BothHandsTokenResult {
  const result: BothHandsTokenResult = {};

  // Compute average palm normal if both hands have normals
  if (leftResult.palmNormal?.normal && rightResult.palmNormal?.normal) {
    const ln = leftResult.palmNormal.normal;
    const rn = rightResult.palmNormal.normal;
    result.palmsNormal = {
      normal: {
        x: (ln.x + rn.x) / 2,
        y: (ln.y + rn.y) / 2,
        z: (ln.z + rn.z) / 2,
      },
    };
  }

  return result;
}

// === Controller class ===

export class HandTokenizerController implements MotionController {
  private options: HandTokenizerOptions = {};
  private disposers: (() => void)[] = [];

  // DOM elements
  private root: HTMLDivElement;
  private hitboxOverlay: HTMLDivElement;
  private renderCanvas: HTMLCanvasElement;
  private renderCtx: CanvasRenderingContext2D;

  private params: MotionControllerParameters;

  // Wrist rotation tracking for angular velocity
  private prevWristRotationLeft: number | undefined = undefined;
  private prevWristRotationRight: number | undefined = undefined;

  // Pinch duration tracking per hand (timestamp when pinch started, undefined = not active)
  private pinchStartLeft: {
    indexFinger?: number;
    indexMiddleFingers?: number;
    allFingers?: number;
  } = {};
  private pinchStartRight: {
    indexFinger?: number;
    indexMiddleFingers?: number;
    allFingers?: number;
  } = {};

  // Wrist position tracking for velocity computation
  private prevWristWorldLeft: { x: number; y: number; z: number } | undefined =
    undefined;
  private prevWristWorldRight: { x: number; y: number; z: number } | undefined =
    undefined;
  private prevWristScreenLeft: { x: number; y: number; z: number } | undefined =
    undefined;
  private prevWristScreenRight:
    | { x: number; y: number; z: number }
    | undefined = undefined;
  private prevFrameTimestamp: number | undefined = undefined;

  // Settled detection: timestamp when hand first went below speed threshold (undefined = currently fast)
  private settledSinceLeft: number | undefined = undefined;
  private settledSinceRight: number | undefined = undefined;

  // Pinch event state tracking (was active last frame)
  private prevTwoFingerPinch = false;
  private prevAllFingersPinch = false;

  constructor(params: MotionControllerParameters) {
    this.params = params;
    const { root: parentRoot } = params;

    if (parentRoot === undefined) {
      throw new Error("root is required");
    }

    // Per-instance hash param listener
    this.disposers.push(
      addEventListenerHashParamJson(
        optionsKey,
        (options: HandTokenizerOptions | undefined) => {
          const prev = this.options;
          this.options = (options as HandTokenizerOptions) || {};

          if (!this.options?.enable && prev?.enable) {
            this.root.remove();
          } else if (this.options?.enable && !prev?.enable) {
            parentRoot.appendChild(this.root);
          }
        },
      ),
    );

    // listen for options changing from metaframe inputs
    this.disposers.push(
      this.params.iostream?.onInputs((inputs) => {
        if (inputs[optionsKey]) {
          setHashParamValueJsonInWindow(
            optionsKey,
            inputs[optionsKey] as HandTokenizerOptions,
          );
        }
      }),
    );

    // === DOM Setup ===
    this.root = document.createElement("div");
    this.root.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:500;";
    if (this.options?.enable !== false) {
      parentRoot.appendChild(this.root);
    }

    // Canvas for palm normal / two-hand angle rendering
    this.renderCanvas = document.createElement("canvas");
    this.renderCanvas.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;";
    this.renderCtx = this.renderCanvas.getContext("2d")!;
    this.root.appendChild(this.renderCanvas);

    // Hitbox overlay (for debug rendering)
    this.hitboxOverlay = document.createElement("div");
    this.hitboxOverlay.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;";
    this.root.appendChild(this.hitboxOverlay);
  }

  dispose(): void {
    while (this.disposers.length > 0) {
      this.disposers.pop()?.();
    }
    this.root.remove();
  }

  private shouldShowToken(name: HandTokenName): boolean {
    if (!this.options?.debug) return false;
    const key = TOKEN_DEBUG_KEY[name];
    return key ? !!this.options[key] : false;
  }

  private updatePinchDurations(
    result: SingleHandTokenResult,
    pinchStart: {
      indexFinger?: number;
      indexMiddleFingers?: number;
      allFingers?: number;
    },
    now: number,
  ): void {
    if (result.pinchIndexFinger) {
      pinchStart.indexFinger ??= now;
      result.pinchIndexFinger.duration = now - pinchStart.indexFinger;
    } else {
      pinchStart.indexFinger = undefined;
    }
    if (result.pinchIndexMiddleFingers) {
      pinchStart.indexMiddleFingers ??= now;
      result.pinchIndexMiddleFingers.duration = now -
        pinchStart.indexMiddleFingers;
    } else {
      pinchStart.indexMiddleFingers = undefined;
    }
    if (result.pinchAllFingers) {
      pinchStart.allFingers ??= now;
      result.pinchAllFingers.duration = now - pinchStart.allFingers;
    } else {
      pinchStart.allFingers = undefined;
    }
  }

  private renderPalmNormals(
    leftClassified: ClassifyHandResult | undefined,
    rightClassified: ClassifyHandResult | undefined,
    isLeftClenched: boolean,
    isRightClenched: boolean,
    canvasBounds: CanvasBoundsType | undefined,
  ): void {
    const rc = this.renderCanvas;
    const ctx = this.renderCtx;
    const rootRect = this.root.getBoundingClientRect();
    const dpr = devicePixelRatio;

    // Resize canvas
    const cw = Math.round(rootRect.width * dpr);
    const ch = Math.round(rootRect.height * dpr);
    if (rc.width !== cw || rc.height !== ch) {
      rc.width = cw;
      rc.height = ch;
    }
    ctx.clearRect(0, 0, rc.width, rc.height);

    const flip = globalThis.options?.cameraFlipHorizontal;

    // Determine visible (non-clenched) hands
    const leftVisible = leftClassified && !isLeftClenched;
    const rightVisible = rightClassified && !isRightClenched;

    const visibleHands: {
      classified: ClassifyHandResult;
      isLeft: boolean;
    }[] = [];
    if (leftVisible) {
      visibleHands.push({ classified: leftClassified!, isLeft: true });
    }
    if (rightVisible) {
      visibleHands.push({ classified: rightClassified!, isLeft: false });
    }

    if (visibleHands.length === 0) return;

    const arrowLength = 150 * dpr;

    const renderNormalArrow = (
      classified: ClassifyHandResult,
      isLeft: boolean,
    ) => {
      const start = mapNormalizedToCanvas(
        classified.palmCenterScreen,
        canvasBounds,
        rootRect,
        dpr,
        !!flip,
      );
      const normal = classified.palmNormalArray;
      const normalX = -normal[0];
      const xMul = flip ? (isLeft ? 1 : -1) : isLeft ? -1 : 1;
      const yMul = flip ? (isLeft ? -1 : 1) : isLeft ? 1 : -1;
      const endX = start.x + normalX * arrowLength * xMul;
      const endY = start.y + normal[1] * arrowLength * yMul;

      drawArrowOnCanvas(ctx, start.x, start.y, endX, endY, dpr);
    };

    if (visibleHands.length === 1 && this.options.renderSinglePalmNormal) {
      const hand = visibleHands[0];
      renderNormalArrow(hand.classified, hand.isLeft);

      // Emit single-hand rotation output
      normalToEulerAngles(hand.classified.palmNormalArray);
      this.params.iostream?.setOutputs({
        "3dAngleEuler": hand.classified.palmNormalArray,
      });
    } else if (visibleHands.length >= 2) {
      if (this.options.renderBothPalmNormals) {
        for (const hand of visibleHands) {
          renderNormalArrow(hand.classified, hand.isLeft);
        }
      }

      if (this.options.renderTwoHandAngle) {
        const hand1 = visibleHands[0];
        const hand2 = visibleHands[1];

        // Draw dashed line between palm centers
        const p1 = mapNormalizedToCanvas(
          hand1.classified.palmCenterScreen,
          canvasBounds,
          rootRect,
          dpr,
          !!flip,
        );
        const p2 = mapNormalizedToCanvas(
          hand2.classified.palmCenterScreen,
          canvasBounds,
          rootRect,
          dpr,
          !!flip,
        );

        ctx.strokeStyle = "#87CEEB";
        ctx.lineWidth = 3 * dpr;
        ctx.setLineDash([10 * dpr, 5 * dpr]);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
        ctx.setLineDash([]);

        // Circles at palm centers
        ctx.fillStyle = "#87CEEB";
        ctx.beginPath();
        ctx.arc(p1.x, p1.y, 8 * dpr, 0, 2 * Math.PI);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p2.x, p2.y, 8 * dpr, 0, 2 * Math.PI);
        ctx.fill();

        // Compute and emit two-hand rotation output
        const wl1 = hand1.classified.palmCenterScreen;
        const wl2 = hand2.classified.palmCenterScreen;
        // Direction vector between world-space palm centers
        const pc1 = hand1.classified.palmNormalArray;
        const pc2 = hand2.classified.palmNormalArray;
        // Use palm centers from world landmarks for 3D direction
        // We don't have world palm centers directly, but we can compute
        // the direction from the two palm normals' average as well as 2D angle
        const angle2d = calculate2DAngle(wl1, wl2);

        this.params.iostream?.setOutputs({
          "2dAngle": angle2d,
        });
      }
    }
  }

  onInputs(inputs: CombinedResults): CombinedResults | undefined {
    if (!this.options?.enable && this.options?.enable !== undefined) {
      return inputs;
    }

    const handData = inputs.hands;
    if (!handData) {
      // PinchUp travels with returned combined results (final overlay setOutputs overwrites
      // any standalone setOutputs from this controller).
      const pinchWhenNoHands: Pick<
        CombinedResults,
        "PinchTwoFingerEvent" | "PinchAllFingersEvent"
      > = {};
      if (this.prevTwoFingerPinch) {
        pinchWhenNoHands.PinchTwoFingerEvent = { event: PinchUp };
      }
      if (this.prevAllFingersPinch) {
        pinchWhenNoHands.PinchAllFingersEvent = { event: PinchUp };
      }
      this.prevTwoFingerPinch = false;
      this.prevAllFingersPinch = false;
      this.prevWristWorldLeft = undefined;
      this.prevWristWorldRight = undefined;
      this.prevWristScreenLeft = undefined;
      this.prevWristScreenRight = undefined;
      this.prevFrameTimestamp = undefined;
      this.settledSinceLeft = undefined;
      this.settledSinceRight = undefined;

      this.hitboxOverlay.innerHTML = "";
      this.renderCtx.clearRect(
        0,
        0,
        this.renderCanvas.width,
        this.renderCanvas.height,
      );
      return { ...inputs, ...pinchWhenNoHands };
    }

    const [worldLeft, worldRight] = getCorrectedHandLandmarks(handData, true);
    const [screenLeft, screenRight] = getCorrectedHandLandmarks(
      handData,
      false,
    );

    // Read clench data from the pipeline (already computed by hands.ts)
    const clench: ClenchData | undefined = inputs.clench;

    const leftClenchSide = clench
      ? {
        meanClenchAngle: clench.meanClenchAngleLeft,
        meanClenchAngleNormalized: clench.meanClenchAngleLeftNormalized,
        isClench: clench.isClenchLeft,
      }
      : undefined;
    const rightClenchSide = clench
      ? {
        meanClenchAngle: clench.meanClenchAngleRight,
        meanClenchAngleNormalized: clench.meanClenchAngleRightNormalized,
        isClench: clench.isClenchRight,
      }
      : undefined;

    const leftClassified = worldLeft && screenLeft
      ? classifyHand(
        worldLeft,
        screenLeft,
        this.prevWristRotationLeft,
        leftClenchSide,
      )
      : undefined;
    const rightClassified = worldRight && screenRight
      ? classifyHand(
        worldRight,
        screenRight,
        this.prevWristRotationRight,
        rightClenchSide,
      )
      : undefined;

    // Track wrist rotation for next frame's angular velocity
    this.prevWristRotationLeft = leftClassified?.wristRotationAngle;
    this.prevWristRotationRight = rightClassified?.wristRotationAngle;

    // Compute wrist velocities (world + screen) and gate pinches on world speed
    const now = inputs.timestamp ?? performance.now();
    const dtSec = this.prevFrameTimestamp !== undefined
      ? (now - this.prevFrameTimestamp) / 1000
      : 0;
    if (dtSec > 0.001) {
      // Left hand velocities + settled detection
      if (leftClassified) {
        if (worldLeft && this.prevWristWorldLeft) {
          const vw = {
            x: (worldLeft[WRIST].x - this.prevWristWorldLeft.x) / dtSec,
            y: (worldLeft[WRIST].y - this.prevWristWorldLeft.y) / dtSec,
            z: (worldLeft[WRIST].z - this.prevWristWorldLeft.z) / dtSec,
          };
          leftClassified.result.velocityWorld = vw;
          const maxAxis = Math.max(
            Math.abs(vw.x),
            Math.abs(vw.y),
            Math.abs(vw.z),
          );
          if (PINCH_MAX_HAND_SPEED > 0 && maxAxis > PINCH_MAX_HAND_SPEED) {
            this.settledSinceLeft = undefined;
          } else {
            this.settledSinceLeft ??= now;
          }
        } else {
          // First frame with this hand — not yet settled
          this.settledSinceLeft = undefined;
        }
        if (screenLeft && this.prevWristScreenLeft) {
          leftClassified.result.velocityScreen = {
            x: (screenLeft[WRIST].x - this.prevWristScreenLeft.x) / dtSec,
            y: (screenLeft[WRIST].y - this.prevWristScreenLeft.y) / dtSec,
            z: (screenLeft[WRIST].z - this.prevWristScreenLeft.z) / dtSec,
          };
        }
        const settled = this.settledSinceLeft !== undefined &&
          now - this.settledSinceLeft >= PINCH_SETTLE_TIME;
        leftClassified.result.isSettled = settled;
        if (!settled) {
          leftClassified.result.pinch = undefined;
          leftClassified.result.pinchIndexFinger = undefined;
          leftClassified.result.pinchIndexMiddleFingers = undefined;
          leftClassified.result.pinchAllFingers = undefined;
        }
      } else {
        this.settledSinceLeft = undefined;
      }
      // Right hand velocities + settled detection
      if (rightClassified) {
        if (worldRight && this.prevWristWorldRight) {
          const vw = {
            x: (worldRight[WRIST].x - this.prevWristWorldRight.x) / dtSec,
            y: (worldRight[WRIST].y - this.prevWristWorldRight.y) / dtSec,
            z: (worldRight[WRIST].z - this.prevWristWorldRight.z) / dtSec,
          };
          rightClassified.result.velocityWorld = vw;
          const maxAxis = Math.max(
            Math.abs(vw.x),
            Math.abs(vw.y),
            Math.abs(vw.z),
          );
          if (PINCH_MAX_HAND_SPEED > 0 && maxAxis > PINCH_MAX_HAND_SPEED) {
            this.settledSinceRight = undefined;
          } else {
            this.settledSinceRight ??= now;
          }
        } else {
          this.settledSinceRight = undefined;
        }
        if (screenRight && this.prevWristScreenRight) {
          rightClassified.result.velocityScreen = {
            x: (screenRight[WRIST].x - this.prevWristScreenRight.x) / dtSec,
            y: (screenRight[WRIST].y - this.prevWristScreenRight.y) / dtSec,
            z: (screenRight[WRIST].z - this.prevWristScreenRight.z) / dtSec,
          };
        }
        const settled = this.settledSinceRight !== undefined &&
          now - this.settledSinceRight >= PINCH_SETTLE_TIME;
        rightClassified.result.isSettled = settled;
        if (!settled) {
          rightClassified.result.pinch = undefined;
          rightClassified.result.pinchIndexFinger = undefined;
          rightClassified.result.pinchIndexMiddleFingers = undefined;
          rightClassified.result.pinchAllFingers = undefined;
        }
      } else {
        this.settledSinceRight = undefined;
      }
    }
    this.prevWristWorldLeft = worldLeft
      ? { x: worldLeft[WRIST].x, y: worldLeft[WRIST].y, z: worldLeft[WRIST].z }
      : undefined;
    this.prevWristWorldRight = worldRight
      ? {
        x: worldRight[WRIST].x,
        y: worldRight[WRIST].y,
        z: worldRight[WRIST].z,
      }
      : undefined;
    this.prevWristScreenLeft = screenLeft
      ? {
        x: screenLeft[WRIST].x,
        y: screenLeft[WRIST].y,
        z: screenLeft[WRIST].z,
      }
      : undefined;
    this.prevWristScreenRight = screenRight
      ? {
        x: screenRight[WRIST].x,
        y: screenRight[WRIST].y,
        z: screenRight[WRIST].z,
      }
      : undefined;
    this.prevFrameTimestamp = now;

    // Update pinch durations per hand
    if (leftClassified) {
      this.updatePinchDurations(
        leftClassified.result,
        this.pinchStartLeft,
        now,
      );
    } else {
      this.pinchStartLeft = {};
    }
    if (rightClassified) {
      this.updatePinchDurations(
        rightClassified.result,
        this.pinchStartRight,
        now,
      );
    } else {
      this.pinchStartRight = {};
    }

    // Emit pinch events
    const twoFingerCentroid =
      leftClassified?.result.pinchIndexFinger?.centroid ??
        rightClassified?.result.pinchIndexFinger?.centroid;
    const allFingersCentroid =
      leftClassified?.result.pinchAllFingers?.centroid ??
        rightClassified?.result.pinchAllFingers?.centroid;

    const curTwoFinger = !!twoFingerCentroid;
    const curAllFingers = !!allFingersCentroid;

    const pinchForCombined: Pick<
      CombinedResults,
      "PinchTwoFingerEvent" | "PinchAllFingersEvent"
    > = {};

    const debug = !!this.options?.debug;

    if (curTwoFinger) {
      pinchForCombined.PinchTwoFingerEvent = {
        event: this.prevTwoFingerPinch ? PinchMove : PinchDown,
        centroid: twoFingerCentroid,
      } satisfies PinchGestureStreamOutput;
    } else if (this.prevTwoFingerPinch) {
      pinchForCombined.PinchTwoFingerEvent = { event: PinchUp };
    }

    if (curAllFingers) {
      pinchForCombined.PinchAllFingersEvent = {
        event: this.prevAllFingersPinch ? PinchMove : PinchDown,
        centroid: allFingersCentroid,
      } satisfies PinchGestureStreamOutput;
    } else if (this.prevAllFingersPinch) {
      pinchForCombined.PinchAllFingersEvent = { event: PinchUp };
    }

    if (
      debug &&
      (pinchForCombined.PinchTwoFingerEvent ||
        pinchForCombined.PinchAllFingersEvent)
    ) {
      console.log("[HandTokenizer] pinch events:", pinchForCombined);
    }

    this.prevTwoFingerPinch = curTwoFinger;
    this.prevAllFingersPinch = curAllFingers;

    // Build HandTokenizerResults
    const bothResult = leftClassified && rightClassified
      ? classifyBothHands(
        leftClassified.result,
        leftClassified.fullHitbox,
        rightClassified.result,
        rightClassified.fullHitbox,
      )
      : undefined;

    const handTokens: HandTokenizerResults = {
      left: leftClassified?.result,
      right: rightClassified?.result,
      both: bothResult,
    };

    // Palm normal / two-hand angle canvas rendering
    const anyRenderEnabled = this.options.renderSinglePalmNormal ||
      this.options.renderBothPalmNormals ||
      this.options.renderTwoHandAngle;
    if (anyRenderEnabled) {
      this.renderPalmNormals(
        leftClassified,
        rightClassified,
        !!clench?.isClenchLeft,
        !!clench?.isClenchRight,
        inputs.canvasBounds,
      );
    } else {
      if (this.renderCanvas.width > 0) {
        this.renderCtx.clearRect(
          0,
          0,
          this.renderCanvas.width,
          this.renderCanvas.height,
        );
      }
    }

    // Debug overlay rendering
    if (this.options?.debug) {
      this.renderHitboxOverlay(handTokens, inputs.canvasBounds);
    } else {
      this.hitboxOverlay.innerHTML = "";
    }

    return {
      ...inputs,
      handTokens,
      ...pinchForCombined,
    };
  }

  // === Debug rendering ===

  private renderHitboxOverlay(
    handTokens: HandTokenizerResults,
    canvasBounds: CanvasBoundsType | undefined,
  ): void {
    this.hitboxOverlay.innerHTML = "";
    const containerRect = this.root.getBoundingClientRect();

    const addBox = (
      box: HitBox,
      color: string,
      label: string,
      yOffset: number,
    ) => {
      const pct = toScreenPercent(box, canvasBounds, containerRect);
      if (pct.width < 0.5 || pct.height < 0.5) return;
      const div = document.createElement("div");
      div.style.cssText = `
        position: absolute;
        left: ${pct.left.toFixed(2)}%;
        top: ${pct.top.toFixed(2)}%;
        width: ${pct.width.toFixed(2)}%;
        height: ${pct.height.toFixed(2)}%;
        background: ${color};
        border: 1.5px solid rgba(255,255,255,0.6);
        border-radius: 3px;
        box-sizing: border-box;
      `;
      const span = document.createElement("span");
      span.style.cssText =
        `position:absolute;top:${
          2 + yOffset * 12
        }px;left:2px;font-family:monospace;font-size:9px;` +
        "color:#fff;text-shadow:0 0 3px #000;line-height:1.2;pointer-events:none;white-space:nowrap;";
      span.textContent = label;
      div.appendChild(span);
      this.hitboxOverlay.appendChild(div);
    };

    const addNormalArrow = (
      point: { x: number; y: number },
      normal: { x: number; y: number; z: number },
      color: string,
      label: string,
    ) => {
      const pct = toScreenPointPercent(point, canvasBounds, containerRect);
      const arrowLen = 60;
      const angle2D = Math.atan2(-normal.y, normal.x) * (180 / Math.PI);
      const solidColor = color.replace(/[\d.]+\)$/, "0.8)");

      const arrow = document.createElement("div");
      arrow.style.cssText =
        `position:absolute;left:${pct.left.toFixed(2)}%;top:${
          pct.top.toFixed(
            2,
          )
        }%;` +
        `width:${arrowLen}px;height:2px;background:${solidColor};` +
        `transform-origin:0% 50%;transform:rotate(${
          angle2D.toFixed(
            1,
          )
        }deg);pointer-events:none;`;
      this.hitboxOverlay.appendChild(arrow);

      const head = document.createElement("div");
      head.style.cssText =
        `position:absolute;left:${pct.left.toFixed(2)}%;top:${
          pct.top.toFixed(
            2,
          )
        }%;pointer-events:none;` +
        `width:0;height:0;border-left:8px solid ${solidColor};border-top:4px solid transparent;border-bottom:4px solid transparent;` +
        `transform-origin:-${arrowLen}px 50%;transform:translate(${arrowLen}px,-4px) rotate(${
          angle2D.toFixed(
            1,
          )
        }deg);`;
      this.hitboxOverlay.appendChild(head);

      const dot = document.createElement("div");
      dot.style.cssText =
        `position:absolute;left:${pct.left.toFixed(2)}%;top:${
          pct.top.toFixed(
            2,
          )
        }%;` +
        `width:8px;height:8px;background:${solidColor};border-radius:50%;` +
        `transform:translate(-50%,-50%);pointer-events:none;`;
      this.hitboxOverlay.appendChild(dot);

      const span = document.createElement("span");
      span.style.cssText =
        `position:absolute;left:${pct.left.toFixed(2)}%;top:calc(${
          pct.top.toFixed(
            2,
          )
        }% + 10px);` +
        "font-family:monospace;font-size:9px;color:#fff;text-shadow:0 0 3px #000;" +
        "line-height:1.2;pointer-events:none;white-space:nowrap;";
      span.textContent = label;
      this.hitboxOverlay.appendChild(span);
    };

    const addDot = (
      point: { x: number; y: number },
      color: string,
      label: string,
    ) => {
      const pct = toScreenPointPercent(point, canvasBounds, containerRect);
      const solidColor = color.replace(/[\d.]+\)$/, "0.8)");
      const dot = document.createElement("div");
      dot.style.cssText =
        `position:absolute;left:${pct.left.toFixed(2)}%;top:${
          pct.top.toFixed(
            2,
          )
        }%;` +
        `width:12px;height:12px;background:${solidColor};border-radius:50%;` +
        `transform:translate(-50%,-50%);pointer-events:none;`;
      const span = document.createElement("span");
      span.style.cssText =
        "position:absolute;top:14px;left:-20px;font-family:monospace;font-size:9px;" +
        "color:#fff;text-shadow:0 0 3px #000;white-space:nowrap;pointer-events:none;";
      span.textContent = label;
      dot.appendChild(span);
      this.hitboxOverlay.appendChild(dot);
    };

    const renderSide = (
      result: SingleHandTokenResult | undefined,
      sideLabel: string,
    ) => {
      if (!result) return;
      let yIdx = 0;
      const wholeHandBox = result.wholeHand?.hitbox;

      if (this.shouldShowToken("wholeHand") && result.wholeHand?.hitbox) {
        addBox(
          result.wholeHand.hitbox,
          TOKEN_COLORS.wholeHand,
          `${sideLabel}:wholeHand`,
          yIdx++,
        );
      }

      if (
        this.shouldShowToken("wristRotation") &&
        result.wristRotation &&
        wholeHandBox
      ) {
        const label = `${sideLabel}:wristRotation a:${
          (
            result.wristRotation.angle ?? 0
          ).toFixed(2)
        }`;
        addBox(wholeHandBox, TOKEN_COLORS.wristRotation, label, yIdx++);
      }

      if (
        this.shouldShowToken("wristAngle") &&
        result.wristAngle &&
        wholeHandBox
      ) {
        const label = `${sideLabel}:wristAngle a:${
          (
            result.wristAngle.angle ?? 0
          ).toFixed(
            2,
          )
        } av:${(result.wristAngle.angularVelocity ?? 0).toFixed(3)}`;
        addBox(wholeHandBox, TOKEN_COLORS.wristAngle, label, yIdx++);
      }

      if (this.shouldShowToken("fist") && result.fist?.hitbox) {
        addBox(
          result.fist.hitbox,
          TOKEN_COLORS.fist,
          `${sideLabel}:fist`,
          yIdx++,
        );
      }

      if (this.shouldShowToken("openPalm") && result.openPalm && wholeHandBox) {
        const label = `${sideLabel}:openPalm ${
          result.openPalm.surfaceVisible ?? ""
        }`;
        addBox(wholeHandBox, TOKEN_COLORS.openPalm, label, yIdx++);
      }

      if (this.shouldShowToken("flatPalm") && result.flatPalm && wholeHandBox) {
        const label = `${sideLabel}:flatPalm ${
          result.flatPalm.surfaceVisible ?? ""
        }`;
        addBox(wholeHandBox, TOKEN_COLORS.flatPalm, label, yIdx++);
      }

      if (this.shouldShowToken("pointing") && result.pointing) {
        if (result.pointing.hitbox) {
          addBox(
            result.pointing.hitbox,
            TOKEN_COLORS.pointing,
            `${sideLabel}:pointing`,
            yIdx++,
          );
        }
        if (result.pointing.centroid) {
          addDot(
            result.pointing.centroid,
            TOKEN_COLORS.pointing,
            `${sideLabel}:pointing`,
          );
        }
      }

      if (this.shouldShowToken("peace") && result.peace) {
        const box = result.peace.hitbox ?? wholeHandBox;
        if (box) addBox(box, TOKEN_COLORS.peace, `${sideLabel}:peace`, yIdx++);
      }
      if (this.shouldShowToken("threeUp") && result.threeUp) {
        const box = result.threeUp.hitbox ?? wholeHandBox;
        if (box) {
          addBox(box, TOKEN_COLORS.threeUp, `${sideLabel}:threeUp`, yIdx++);
        }
      }
      if (this.shouldShowToken("fourUp") && result.fourUp) {
        const box = result.fourUp.hitbox ?? wholeHandBox;
        if (box) {
          addBox(box, TOKEN_COLORS.fourUp, `${sideLabel}:fourUp`, yIdx++);
        }
      }
      if (this.shouldShowToken("thumbUp") && result.thumbUp) {
        const box = result.thumbUp.hitbox ?? wholeHandBox;
        if (box) {
          addBox(box, TOKEN_COLORS.thumbUp, `${sideLabel}:thumbUp`, yIdx++);
        }
      }
      if (this.shouldShowToken("ok") && result.ok) {
        const box = result.ok.hitbox ?? wholeHandBox;
        if (box) addBox(box, TOKEN_COLORS.ok, `${sideLabel}:ok`, yIdx++);
      }

      // Two-finger pinch: thumb + index
      if (
        this.options?.debugPinchTwoFinger &&
        result.pinchIndexFinger?.centroid
      ) {
        addDot(
          result.pinchIndexFinger.centroid,
          TOKEN_COLORS.pinch,
          `${sideLabel}:pinch2 d:${
            (
              result.pinchIndexFinger.duration ?? 0
            ).toFixed(0)
          }ms`,
        );
      }

      // Multi-finger pinch: use the highest-level active pinch centroid
      if (this.options?.debugPinchMultiFinger) {
        const multi = result.pinchAllFingers ?? result.pinchIndexMiddleFingers;
        if (multi?.centroid) {
          const label = result.pinchAllFingers
            ? `${sideLabel}:pinchAll`
            : `${sideLabel}:pinchMulti`;
          addDot(
            multi.centroid,
            "rgba(255,180,60,0.35)",
            `${label} d:${(multi.duration ?? 0).toFixed(0)}ms`,
          );
        }
      }
    };

    renderSide(handTokens.left, "L");
    renderSide(handTokens.right, "R");

    if (handTokens.both && this.options?.debug && this.options?.debugBimanual) {
      if (handTokens.both.palmsNormal?.normal) {
        const leftBox = handTokens.left?.wholeHand?.hitbox;
        const rightBox = handTokens.right?.wholeHand?.hitbox;
        if (leftBox && rightBox) {
          const leftCenter = boxCentroid(leftBox);
          const rightCenter = boxCentroid(rightBox);
          const midpoint = {
            x: (leftCenter.x + rightCenter.x) / 2,
            y: (leftCenter.y + rightCenter.y) / 2,
          };
          addNormalArrow(
            midpoint,
            handTokens.both.palmsNormal.normal,
            "rgba(220,220,80,0.35)",
            "B:palmsNormal",
          );
        }
      }
    }
  }
}
